/**
 * @file src/components/atoms/BarDial/SplitBarDial.tsx
 * @description This file defines a SplitBarDial component that displays two values using a horizontal bar split into upper and lower halves with animation and drag functionality.
 * @module SplitBarDial
 */

import React, { FC, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import Tooltip from '@/components/atoms/Tooltip/Tooltip';

interface SplitBarDialProps {
  topValue: number;
  bottomValue: number;
  onChangeTop?: (value: number) => void;
  onChangeBottom?: (value: number) => void;
  topFillColor?: string;
  topFillColorOpacity?: string;
  bottomFillColor?: string;
  bottomFillColorOpacity?: string;
  topTrackColor?: string;
  topTrackOpacity?: string;
  bottomTrackColor?: string;
  bottomTrackOpacity?: string;
  topBorderColor?: string;
  topBorderOpacity?: string;
  bottomBorderColor?: string;
  bottomBorderOpacity?: string;
  borderWidth?: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  roundedness?: string;
  handleColor?: string;
  handleColorOpacity?: string;
  editable?: boolean;
  hoverContent?: string | React.ReactNode;
}

const SplitBarDial: FC<SplitBarDialProps> = ({
  topValue,
  bottomValue,
  onChangeTop,
  onChangeBottom,
  bottomFillColor = 'bg-error',
  bottomFillColorOpacity = 'bg-opacity-100',
  topFillColor = 'bg-accent',
  topFillColorOpacity = 'bg-opacity-100',
  bottomTrackColor = 'bg-error',
  bottomTrackOpacity = 'bg-opacity-20',
  topTrackColor = 'bg-accent',
  topTrackOpacity = 'bg-opacity-5',
  bottomBorderColor = 'border-transparent',
  bottomBorderOpacity = 'border-opacity-0',
  topBorderColor = 'border-transparent',
  topBorderOpacity = 'border-opacity-0',
  borderWidth = 'border-0',
  width: size = 'w-full',
  height = 'h-md',
  roundedness = 'rounded-2xs',
  className = '',
  handleColor = 'bg-accent',
  handleColorOpacity = 'bg-opacity-100',
  editable = false,
  hoverContent,
}) => {
  const barRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback(
    (e: MouseEvent, onChange: ((value: number) => void) | undefined) => {
      if (!barRef.current || !onChange) return;

      const rect = barRef.current.getBoundingClientRect();
      const newValue = Math.round(((e.clientX - rect.left) / rect.width) * 100);
      onChange(Math.min(100, Math.max(0, newValue)));
    },
    [],
  );

  const handleMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', (e) =>
      handleMouseMove(e, onChangeTop),
    );
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove, onChangeTop]);

  const handleMouseDownTop = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      if (editable) {
        handleMouseMove(e.nativeEvent, onChangeTop);
        document.addEventListener('mousemove', (e) =>
          handleMouseMove(e, onChangeTop),
        );
        document.addEventListener('mouseup', handleMouseUp);
      }
    },
    [handleMouseMove, handleMouseUp, onChangeTop, editable],
  );

  const handleMouseDownBottom = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.preventDefault();
      if (editable) {
        handleMouseMove(e.nativeEvent, onChangeBottom);
        document.addEventListener('mousemove', (e) =>
          handleMouseMove(e, onChangeBottom),
        );
        document.addEventListener('mouseup', handleMouseUp);
      }
    },
    [handleMouseMove, handleMouseUp, onChangeBottom, editable],
  );

  const handleClickTop = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (editable) {
        handleMouseMove(e.nativeEvent, onChangeTop);
      }
    },
    [handleMouseMove, onChangeTop, editable],
  );

  const handleClickBottom = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (editable) {
        handleMouseMove(e.nativeEvent, onChangeBottom);
      }
    },
    [handleMouseMove, onChangeBottom, editable],
  );

  const barDialContent = (
    <div
      ref={barRef}
      className={`relative flex flex-col justify-center overflow-hidden ${className} ${size} ${height} ${roundedness} ${borderWidth} ${
        editable ? 'cursor-pointer' : 'cursor-default'
      }`}
    >
      <div className="relative flex-1 w-full">
        <div
          className={`relative w-full h-1/2 overflow-hidden ${topTrackColor} ${topTrackOpacity} ${topBorderColor} ${topBorderOpacity}`}
          onMouseDown={editable ? handleMouseDownTop : undefined}
          onClick={editable ? handleClickTop : undefined}
        >
          <motion.div
            initial={{ width: '0%' }}
            animate={{ width: `${topValue}%` }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
            className={`h-full ${topFillColor} ${topFillColorOpacity}`}
            style={{ position: 'absolute', top: 0, left: 0 }}
          />
          {editable && (
            <div
              className={`absolute top-0 right-0 w-1 h-full ${handleColor} ${handleColorOpacity} cursor-ew-resize`}
              style={{ transform: 'translateX(50%)' }}
            />
          )}
        </div>
        <div
          className={`relative w-full h-1/2 overflow-hidden ${bottomTrackColor} ${bottomTrackOpacity} ${bottomBorderColor} ${bottomBorderOpacity}`}
          onMouseDown={editable ? handleMouseDownBottom : undefined}
          onClick={editable ? handleClickBottom : undefined}
        >
          <motion.div
            initial={{ width: '0%' }}
            animate={{ width: `${bottomValue}%` }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
            className={`h-full ${bottomFillColor} ${bottomFillColorOpacity}`}
            style={{ position: 'absolute', top: 0, left: 0 }}
          />
          {editable && (
            <div
              className={`absolute top-0 right-0 w-1 h-full ${handleColor} ${handleColorOpacity} cursor-ew-resize`}
              style={{ transform: 'translateX(50%)' }}
            />
          )}
        </div>
      </div>
    </div>
  );

  return hoverContent ? (
    <Tooltip trigger={barDialContent} alignToElementRef={barRef}>
      {hoverContent}
    </Tooltip>
  ) : (
    barDialContent
  );
};

export default SplitBarDial;
